import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiArrowRight, FiChevronDown, FiChevronRight, FiInfo, FiSearch } from 'react-icons/fi';
import menuApi from '../../services/menuapi';
import { assignedMenuIds, buildMenuTree, menuTargets, projectMenus } from './roleMenuTree';
import './RoleSettings.css';

function MenuCheckbox({ partial, ...props }) {
    const input = useRef(null);
    useEffect(() => { if (input.current) input.current.indeterminate = partial; }, [partial]);
    return <input ref={input} type="checkbox" className="role-menu-checkbox" aria-checked={partial ? 'mixed' : props.checked} {...props} />;
}

const MenuRoleAssigner = ({ roleId, onClose, onBusyChange, onDirtyChange }) => {
    const [tree, setTree] = useState([]);
    const [assigned, setAssigned] = useState(new Set());
    const [original, setOriginal] = useState([]);
    const [selected, setSelected] = useState({ available: new Set(), assigned: new Set() });
    const [search, setSearch] = useState({ available: '', assigned: '' });
    const [collapsed, setCollapsed] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [reload, setReload] = useState(0);
    const generation = useRef(0);
    const saveLock = useRef(false);

    useEffect(() => {
        const current = ++generation.current;
        let alive = true;
        setLoading(true); setError(''); setSuccess(''); setTree([]);
        setAssigned(new Set()); setOriginal([]);
        setSelected({ available: new Set(), assigned: new Set() });
        setSearch({ available: '', assigned: '' }); setCollapsed(new Set());
        (async () => {
            try {
                const [menus, response] = await Promise.all([menuApi.getAllRoleMenus(), menuApi.getMenusByRole(roleId, true)]);
                if (response.status !== 'SUCCESS' || !Array.isArray(response.data)) throw new Error(response.message || 'Unable to load assigned menus.');
                // Include existing inactive assignments so saving does not silently revoke them.
                const byId = new Map([...menus, ...response.data].map(menu => [Number(menu.id), menu]));
                const nextTree = buildMenuTree([...byId.values()]);
                const targets = new Set(nextTree.flatMap(menuTargets));
                const nextAssigned = new Set(response.data.map(menu => Number(menu.id)).filter(id => targets.has(id)));
                if (!alive) return;
                setTree(nextTree); setAssigned(nextAssigned);
                setOriginal(assignedMenuIds(nextTree, nextAssigned));
            } catch (err) { if (alive) setError(err.message || 'Unable to load menus. Please retry.'); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; generation.current = current + 1; };
    }, [roleId, reload]);

    const allMenus = useMemo(() => {
        const map = new Map();
        const visit = node => { map.set(node.id, node); node.children.forEach(visit); };
        tree.forEach(visit); return map;
    }, [tree]);
    const canAssign = id => {
        let node = allMenus.get(id);
        while (node) {
            if (Number(node.status) !== 1) return false;
            node = allMenus.get(node.parentId);
        }
        return true;
    };
    const currentIds = assignedMenuIds(tree, assigned);
    const dirty = JSON.stringify(currentIds) !== JSON.stringify(original);
    useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
    const busy = loading || saving;
    const updateSelection = (side, ids, checked) => {
        setSelected(prev => {
            const next = new Set(prev[side]);
            ids.filter(id => side === 'assigned' || canAssign(id)).forEach(id => checked ? next.add(id) : next.delete(id));
            return { ...prev, [side]: next };
        });
    };
    const transfer = side => {
        setAssigned(prev => {
            const next = new Set(prev);
            selected[side].forEach(id => side === 'available' ? next.add(id) : next.delete(id));
            return next;
        });
        setSelected({ available: new Set(), assigned: new Set() }); setSuccess('');
    };
    const cancel = () => {
        if (!saving) onClose?.();
    };
    const save = async () => {
        if (busy || !dirty || saveLock.current) return;
        const current = generation.current;
        saveLock.current = true; setSaving(true); onBusyChange?.(true); setError(''); setSuccess('');
        try {
            const response = await menuApi.assignMenusToRole({ roleId, menuIds: currentIds });
            if (response.status !== 'SUCCESS') throw new Error(response.message || 'Unable to save menus.');
            if (generation.current !== current) return;
            setOriginal(currentIds); setSuccess('Menu assignments saved successfully.');
            setSelected({ available: new Set(), assigned: new Set() });
        } catch (err) { if (generation.current === current) setError(err.message || 'Unable to save menus. Your changes are retained; please retry.'); }
        finally { saveLock.current = false; setSaving(false); onBusyChange?.(false); }
    };

    const renderNode = (node, side, depth = 0) => {
        const ids = node.targets.filter(id => side === 'assigned' || canAssign(id));
        const count = ids.filter(id => selected[side].has(id)).length;
        const isGroup = allMenus.get(node.id).children.length > 0;
        const key = `${side}-${node.id}`;
        const closed = collapsed.has(key) && !search[side].trim();
        return <div key={node.id} className="role-menu-group">
            <div className={`role-menu-row ${isGroup ? 'role-menu-parent' : ''}`} style={{ paddingLeft: 14 + depth * 22 }}>
                {isGroup ? <button type="button" className="role-menu-expand" aria-label={`${closed ? 'Expand' : 'Collapse'} ${node.title}`} aria-expanded={!closed}
                    onClick={() => setCollapsed(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; })}>
                    {closed ? <FiChevronRight /> : <FiChevronDown />}
                </button> : <span className="role-menu-indent" />}
                <MenuCheckbox checked={ids.length > 0 && count === ids.length} partial={count > 0 && count < ids.length}
                    disabled={busy || !ids.length} aria-label={`Select ${node.title} in ${side} menus`}
                    onChange={e => updateSelection(side, ids, e.target.checked)} />
                <span className="role-menu-title">{node.title}</span>
                {Number(node.status) !== 1 && <span className="role-menu-inactive">Inactive</span>}
                {isGroup && <><span className="role-count">{node.targets.length}</span><div className="role-group-actions">
                    <button type="button" disabled={busy || !ids.length} onClick={() => updateSelection(side, ids, true)}>Select All</button>
                    <span>|</span><button type="button" disabled={busy || !count} onClick={() => updateSelection(side, ids, false)}>Clear</button>
                </div></>}
            </div>
            {isGroup && !closed && <>
                {node.ownVisible && <label className="role-menu-row" style={{ paddingLeft: 58 + depth * 22 }}>
                    <MenuCheckbox checked={selected[side].has(node.id)} disabled={busy || (side === 'available' && !canAssign(node.id))}
                        onChange={e => updateSelection(side, [node.id], e.target.checked)} />
                    <span>Open {node.title}</span>
                </label>}
                {node.children.map(child => renderNode(child, side, depth + 1))}
            </>}
        </div>;
    };
    const panel = side => {
        const projected = projectMenus(tree, assigned, side, search[side]);
        const total = projectMenus(tree, assigned, side).flatMap(node => node.targets).length;
        return <section className={`role-menu-panel ${side}`} aria-label={side === 'available' ? 'Available Menus' : 'Assigned Menus'}>
            <div className="role-menu-panel-header"><div className="role-panel-title">{side === 'available' ? 'Available Menus' : 'Assigned Menus'} <span className="role-count">{total}</span></div>
                <div className="role-panel-search"><div className="role-search"><FiSearch /><input value={search[side]} disabled={busy}
                    aria-label={`Search ${side} menus`} placeholder={`Search ${side} menus...`} onChange={e => setSearch(prev => ({ ...prev, [side]: e.target.value }))} /></div>
                    <Button variant="light" className="role-neutral" disabled={busy || !selected[side].size} onClick={() => setSelected(prev => ({ ...prev, [side]: new Set() }))}>Clear selection</Button>
                </div>
            </div>
            <div className="role-menu-scroll" aria-busy={loading}>
                {loading ? <div className="role-menu-empty"><Spinner size="sm" animation="border" /> Loading menus…</div> : projected.length ? projected.map(node => renderNode(node, side)) :
                    <div className="role-menu-empty">{search[side] ? 'No matching menus.' : side === 'assigned' ? 'No menus assigned.' : 'No available menus.'}</div>}
            </div>
        </section>;
    };
    return <div className="role-menu-editor">
        <div className="role-menu-feedback" aria-live="polite">
            {error && <Alert variant="danger">{error} {!tree.length && <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => setReload(value => value + 1)}>Retry</Button>}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
        </div>
        <div className="role-menu-transfer">{panel('available')}<div className="role-transfer-actions">
            <span>{selected.available.size} selected</span><Button variant="success" disabled={busy || !selected.available.size} onClick={() => transfer('available')}>Assign selected <FiArrowRight /></Button>
            <span>{selected.assigned.size} selected</span><Button variant="outline-danger" disabled={busy || !selected.assigned.size} onClick={() => transfer('assigned')}><FiArrowLeft /> Remove selected</Button>
        </div>{panel('assigned')}</div>
        <div className="role-menu-footer"><span><FiInfo /> Select menus, then assign or remove. Changes apply after saving.{dirty && ' Unsaved changes.'}</span>
            <div><Button variant="light" className="role-neutral" disabled={saving} onClick={cancel}>Cancel</Button><Button variant="success" disabled={busy || !tree.length || !dirty} onClick={save}>{saving ? 'Saving…' : 'Save Changes'}</Button></div>
        </div>
    </div>;
};
export default MenuRoleAssigner;
